import { toast } from "sonner";
import { Loader } from "@/shared/ui/Loader";
import { Button } from "@/components/ui/button";


import { useNavigate, useSearch } from "@tanstack/react-router";
import { TicketIcon, PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useCouponsQuery,
  useCouponsSummaryQuery,
  useToggleCouponActiveMutation,
  useDeleteCouponMutation,
} from "../api/queries";
import { CouponsFilter } from "../ui/CouponsFilter";
import { CouponsSummaryCards } from "../ui/CouponsSummaryCards";
import { CouponsTable } from "../ui/CouponsTable";

type CouponsSearch = {
  promotionId?: string;
  isActive?: string;
  search?: string;
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
};

export function CouponsPage() {
  const nav = useNavigate({ from: "/app/loyalty/promotions/coupons" });
  const search = useSearch({
    from: "/app/loyalty/promotions/coupons",
  }) as CouponsSearch;
  const canManage = useActionPermission(["loyalty:update"]);

  const params = {
    promotionId: search.promotionId,
    isActive:
      search.isActive === "true"
        ? ("true" as const)
        : search.isActive === "false"
          ? ("false" as const)
          : undefined,
    search: search.search,
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sortField: (search.sortField ?? "code") as "code" | "createdAt",
    sortOrder: (search.sortOrder ?? "asc") as "asc" | "desc",
  };

  const { data, isLoading } = useCouponsQuery(params);
  const { data: summaryData, isLoading: summaryLoading } =
    useCouponsSummaryQuery();
  const toggleActive = useToggleCouponActiveMutation();
  const deleteCoupon = useDeleteCouponMutation();

  const items = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຄູປອງ",
              to: "/app/loyalty/promotions/coupons",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <TicketIcon className="size-5" />
              ຄູປອງ
            </h2>
            <p className="text-muted-foreground">
              ຈັດການຄູປອງທີ່ຜູກກັບໂປຣໂມຊັນ
            </p>
          </div>
          {canManage && (
            <Button
              onClick={() =>
                nav({ to: "/app/loyalty/promotions/coupons/create" })
              }
            >
              <PlusIcon className="size-4" />
              ສ້າງຄູປອງ
            </Button>
          )}
        </div>

        <div className="mb-4">
          <CouponsSummaryCards
            topCode={summaryData?.topCode ?? null}
            lowInventory={summaryData?.lowInventory ?? 0}
            expiringSoon={summaryData?.expiringSoon ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <CouponsFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <CouponsTable
              data={items}
              isLoading={isLoading}
              offset={params.offset ?? 0}
              limit={params.limit ?? 20}
              totalCount={total}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onEdit={(row) =>
                nav({
                  to: "/app/loyalty/promotions/coupons/$couponId/edit",
                  params: { couponId: row.id },
                })
              }
              onToggleActive={async (id, isActive) => {
                await toast.promise(toggleActive.mutateAsync({ id, isActive }), {
                  loading: "ກຳລັງປ່ຽນສະຖານະ...",
                  success: isActive
                    ? "ເປີດໃຊ້ງານຄູປອງແລ້ວ"
                    : "ປິດໃຊ້ງານຄູປອງແລ້ວ",
                  error: "ປ່ຽນສະຖານະລົ້ມເຫຼວ",
                });
              }}
              onDelete={async (id) => {
                await toast.promise(deleteCoupon.mutateAsync(id), {
                  loading: "ກຳລັງລຶບ...",
                  success: "ລຶບຄູປອງແລ້ວ",
                  error: "ລຶບຄູປອງລົ້ມເຫຼວ",
                });
              }}
            />
          )}
        </div>
      </Main>
    </>
  );
}
